/**
 * Firebase initialization — CLOUD path only.
 *
 * If the VITE_FIREBASE_* env vars are absent we are in local/dev mode: NO
 * Firebase is initialized (calling initializeApp with undefined config throws),
 * the API needs no Authorization header, and sign-in is skipped. The server's
 * dev-bypass injects a fixed reviewer.
 */

import { type FirebaseApp, initializeApp } from "firebase/app";
import {
  type Auth,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
} from "firebase/auth";

const env = import.meta.env;

/**
 * Cloud sign-in method (build-time): "google" (popup, default) or
 * "password" (email/contraseña; accounts are provisioned manually by an
 * admin — the SPA never offers signup or self-service reset).
 */
export type AuthMethod = "google" | "password";
export const AUTH_METHOD: AuthMethod =
  env.VITE_AUTH_METHOD === "password" ? "password" : "google";

/** True only when a full Firebase web config is present (cloud path). */
export const FIREBASE_ENABLED = Boolean(
  env.VITE_FIREBASE_API_KEY &&
    env.VITE_FIREBASE_AUTH_DOMAIN &&
    env.VITE_FIREBASE_PROJECT_ID &&
    env.VITE_FIREBASE_APP_ID,
);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

if (FIREBASE_ENABLED) {
  app = initializeApp({
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  });
  auth = getAuth(app);
}

/** The Firebase Auth instance, or null in local/dev mode. */
export const firebaseAuth: Auth | null = auth;

/** Google sign-in via popup (cloud only). No-op-throws if Firebase is disabled. */
export async function googleSignIn(): Promise<void> {
  if (!firebaseAuth) {
    throw new Error("Firebase no está configurado (modo local/dev).");
  }
  const provider = new GoogleAuthProvider();
  const hd = env.VITE_FIREBASE_HOSTED_DOMAIN;
  if (hd) {
    // Hint Google to restrict the account chooser to the Workspace domain.
    provider.setCustomParameters({ hd });
  }
  await signInWithPopup(firebaseAuth, provider);
}

/**
 * Email/contraseña sign-in (cloud only, AUTH_METHOD === "password").
 * Firebase auth errors (wrong password, unknown user, disabled account…)
 * are normalized to a single generic Spanish message so the form does not
 * disclose which of the two fields failed.
 */
export async function passwordSignIn(email: string, password: string): Promise<void> {
  if (!firebaseAuth) {
    throw new Error("Firebase no está configurado (modo local/dev).");
  }
  try {
    await signInWithEmailAndPassword(firebaseAuth, email, password);
  } catch (err: unknown) {
    const code = (err as { code?: string }).code ?? "";
    if (code.startsWith("auth/")) {
      throw new Error(
        "No se pudo iniciar sesión. Verifique su correo y contraseña; si el problema persiste, contacte a su administrador.",
      );
    }
    throw err;
  }
}

/** Sign out of Firebase (cloud only). */
export async function googleSignOut(): Promise<void> {
  if (firebaseAuth) {
    await fbSignOut(firebaseAuth);
  }
}

/** Fresh ID token for the current user, or null when not signed in / local. */
export async function getIdToken(): Promise<string | null> {
  const user = firebaseAuth?.currentUser;
  if (!user) return null;
  return user.getIdToken();
}
